import { useContext, useMemo } from 'react'
import { LangueCourante } from '../i18n/langue'

/**
 * Un nombre, lisible dans les deux sens d'écriture (L2-10).
 *
 * LE PROBLÈME EXACT que ce composant résout. En arabe, le paragraphe se lit de droite
 * à gauche, mais les CHIFFRES se lisent toujours de gauche à droite. Tant qu'un nombre
 * est nu — « 42 » — l'algorithme bidirectionnel d'Unicode s'en sort seul. Dès qu'un
 * signe ou une unité s'y accroche, il ne s'en sort plus : « +42 % » placé dans un
 * contexte RTL se rend « % 42+ », parce que le « + » et le « % » sont des caractères
 * neutres qui prennent la direction de leur voisin.
 *
 * La correction est de poser `dir="ltr"` sur le nombre ENTIER, signe et unité compris,
 * de sorte qu'il forme un îlot de lecture gauche-droite dans une phrase droite-gauche.
 * C'est exactement ce que fait un tableur arabe.
 *
 * `display: inline-block` accompagne le `dir` : sans lui, un nombre coupé en fin de
 * ligne verrait ses morceaux réordonnés séparément.
 */
export function Nombre({
  valeur,
  decimales = 2,
  unite,
  signe = false,
  annee = false,
}: {
  valeur: number | null | undefined
  /** Nombre de décimales. Les montants en tiennent trois : le millime. */
  decimales?: number
  /** Unité ou symbole accolé : « ha », « % », « DT ». */
  unite?: string
  /** Affiche le « + » des valeurs positives. C'est le cas qui casse en arabe. */
  signe?: boolean
  /**
   * Une année, pas une quantité : aucun séparateur de milliers. Le français n'en met pas à
   * quatre chiffres, mais l'arabe et l'anglais lisaient « 2,015 » pour une plantation de 2015.
   */
  annee?: boolean
}) {
  // La langue seule, pas le dictionnaire : le site vitrine l'affiche aussi (i18n/langue.ts).
  const language = useContext(LangueCourante)

  const format = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
        signDisplay: signe ? 'exceptZero' : 'auto',
        useGrouping: !annee,
      }),
    [language, decimales, signe, annee],
  )

  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) {
    return <span>—</span>
  }

  const rendu = unite === undefined ? format.format(valeur) : `${format.format(valeur)} ${unite}`

  return (
    <span dir="ltr" style={{ display: 'inline-block', unicodeBidi: 'isolate' }}>
      {rendu}
    </span>
  )
}

/**
 * Un pourcentage. C'est le cas nommé par le critère de recette : « +42 % » ne doit pas
 * s'afficher « % 42+ ».
 */
export function Pourcentage({ valeur, decimales = 1, signe = true }: {
  valeur: number | null | undefined
  decimales?: number
  signe?: boolean
}) {
  return <Nombre valeur={valeur} decimales={decimales} unite="%" signe={signe} />
}

/**
 * Une date. Elle se lit aussi de gauche à droite dans les deux langues : « 2026-03-15 »
 * inversé donnerait « 15-03-2026 », qui se lit très bien et veut dire autre chose.
 */
export function DateLisible({ valeur }: { valeur: string | null | undefined }) {
  if (valeur === null || valeur === undefined || valeur === '') {
    return <span>—</span>
  }

  return (
    <span dir="ltr" style={{ display: 'inline-block', unicodeBidi: 'isolate' }}>
      {valeur.length > 10 ? valeur.slice(0, 16).replace('T', ' ') : valeur}
    </span>
  )
}

/**
 * Un fragment technique déjà composé — l'expression d'une norme (« 6 &lt; ph &lt; 8 »),
 * par exemple (§8, migration 0051). Ni un nombre seul ni une date : un texte qui mélange
 * chiffres, lettres et opérateurs de comparaison, mais qui a besoin du même îlot de
 * lecture gauche-droite que <Nombre> et <DateLisible> ci-dessus, pour la même raison
 * qu'eux — sans `dir="ltr"`, « 6 &lt; ph &lt; 8 » se retournerait dans un paragraphe arabe.
 */
export function Expression({ valeur }: { valeur: string }) {
  return (
    <span dir="ltr" style={{ display: 'inline-block', unicodeBidi: 'isolate' }}>
      {valeur}
    </span>
  )
}
